import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import classnames from "classnames";
import { ConditionShape, MessageShape, MessageSenders } from "@eave/shared/types/api";

import DropdownModal from "web_modules/shared/DropdownModal";
import Editable from "web_modules/shared/Editable/index";
import APIClient from "web_modules/APIClient";
import { formatDateTimeString } from "@eave/shared/utils/date-formatter";

const FontAwesome = require("react-fontawesome");
const styles = require("./styles.module.scss");

interface Props {
  id: number;
  appID: number;
  messageThreadID: string;
  internalMessageThreadID: string;

  comment: any;
  messages: any;
  condition: ConditionShape;
  markCommentRead: (commentID: number, messageID: number) => void;
  appendConditionComments: (id: number, comment: MessageShape, internal: boolean) => void;

  loggedInUser: any;
}

// Scrolls to the bottom of the scrollable message thread
const scrollToBottom = ref => {
  if (ref.current && ref.current.clientHeight < ref.current.scrollHeight) {
    ref.current.scrollTop = ref.current.scrollHeight - ref.current.clientHeight;
  }
};

const useScrollBottom = lastMessageID => {
  const ref = React.useRef();

  React.useLayoutEffect(() => {
    scrollToBottom(ref);
  }, [lastMessageID]);
  return ref;
};

enum SENDING_MODE {
  INTERNAL = "internal",
  BORROWER = "borrower",
}

const CommentsModal: React.FC<Props> = ({
  id,
  appID,
  messageThreadID,
  internalMessageThreadID,
  condition,
  comment,
  messages,
  markCommentRead,
  appendConditionComments,
  loggedInUser,
}) => {
  // Controls the styling of the UI components
  // So named to avoid confusion with the types of the messages themselves
  const [sendingMode, setSendingMode] = useState(SENDING_MODE.INTERNAL);

  return (
    <DropdownModal>
      {{
        trigger: <CommentIcon messages={messages || []} />,
        content: (
          <div>
            <div
              className={classnames(
                styles.comments,
                sendingMode === SENDING_MODE.INTERNAL ? styles.internalMode : styles.borrowerMode,
              )}
            >
              <h4 className={styles.commentsHeading}>Comments for condition</h4>
              <p className={styles.commentsSubheading}>{condition}</p>
              <p className={styles.commentsMessageVisibility}>
                <span>Showing all messages</span>
              </p>
              <Comments
                appID={appID}
                commentID={id}
                loggedInUser={loggedInUser}
                internalThreadID={internalMessageThreadID}
                messageThreadID={messageThreadID}
                messages={messages}
                markCommentRead={markCommentRead}
              />
              <div className={styles.sendingModeToggle}>
                <button
                  type="button"
                  onClick={() => setSendingMode(SENDING_MODE.INTERNAL)}
                  className={classnames(
                    styles.sendingModeButton,
                    sendingMode === SENDING_MODE.INTERNAL ? styles.internal : styles.disabled,
                  )}
                >
                  Internal Comment
                </button>
                <button
                  type="button"
                  onClick={() => setSendingMode(SENDING_MODE.BORROWER)}
                  className={classnames(
                    styles.sendingModeButton,
                    sendingMode === SENDING_MODE.BORROWER ? styles.borrower : styles.disabled,
                  )}
                >
                  Borrower Comment
                </button>
                <div className={classnames(styles.sendingModeLabel, "borrower")}>
                  {sendingMode === SENDING_MODE.BORROWER
                    ? "Sending to borrower"
                    : "Sending internally"}
                </div>
              </div>
              <Editable
                autoFocus={false}
                placeholder="Leave a comment"
                clearOnSave
                value=""
                onSave={value => {
                  const internal = sendingMode === SENDING_MODE.INTERNAL;

                  return APIClient.post(`/messages/condition/${id}`, {
                    message: value,
                    internal,
                  }).then(({ message }) => {
                    appendConditionComments(id, message, internal);
                  });
                }}
                acceptedInput="textarea"
              />
            </div>
            {comment && (
              <div className={styles.borrowerComment}>
                <strong>Borrower Comment:</strong> <p>{comment}</p>
              </div>
            )}
          </div>
        ),
      }}
    </DropdownModal>
  );
};

interface CommentIconProps {
  messages: MessageShape[];
}

const isMessageUnreadByOperator: (message: MessageShape) => boolean = ({ read }) => !read;

const CommentIcon: React.FunctionComponent<CommentIconProps> = ({ messages }) => {
  const messagesCount = (messages && messages.length) || 0;
  return (
    <div className={styles.commentIcon}>
      <FontAwesome
        name="comment"
        className={classnames(
          styles.commentIcon,
          messages.filter(m => !m.read).length > 0 && styles.commentUnread,
        )}
      />
      {messagesCount > 0 && <> ({messagesCount})</>}
    </div>
  );
};

const getBubbleText = (senderType: string, senderName: string): string => {
  if (senderType === MessageSenders.Operator) {
    return senderName;
  }

  if (senderType === MessageSenders.Borrower) {
    return "BWR";
  }

  if (senderType === MessageSenders.System) {
    return "TRUS";
  }

  return "Unknown";
};

const getBubbleColor = (
  sentByCurrentUser: boolean,
  sentByOperator: boolean,
  sentBySystem: boolean,
): string => {
  if (sentByCurrentUser) {
    return styles.sentByMe;
  }

  if (sentByOperator) {
    return styles.operatorSender;
  }

  if (sentBySystem) {
    return styles.systemSender;
  }

  return styles.borrowerSender;
};

interface CommentsProps {
  appID: number;
  commentID: number;
  internalThreadID: string;
  messageThreadID: string;
  loggedInUser: any;
  messages: MessageShape[];
  markCommentRead: (commentID: number, messageID: number) => void;
}

const Comments: React.FunctionComponent<CommentsProps> = ({
  appID,
  commentID,
  internalThreadID,
  loggedInUser,
  messages = [],
  markCommentRead,
}) => {
  useEffect(() => {
    messages.filter(isMessageUnreadByOperator).forEach(message => {
      APIClient.post(`/${appID}/messages/${message.id}/read`, {}).then(() => {
        markCommentRead(commentID, message.id);
      });
    });
  }, [appID, commentID, markCommentRead, messages]);

  return (
    <div
      ref={useScrollBottom((messages.length > 0 && messages[messages.length - 1].id) || null)}
      className={styles.thread}
    >
      {messages
        .sort((m1, m2) => {
          const a = moment(m1.created_at);
          const b = moment(m2.created_at);
          return a.isBefore(b) ? -1 : 1;
        })
        .map(({ created_at, id, thread_id, sender_type, sender_id, sender_name, message }) => {
          // Controls styling
          const sentByOperator = sender_type === MessageSenders.Operator;
          const sentByCurrentUser = sender_id === loggedInUser.user_id;
          const isInternalMessage = thread_id === internalThreadID;

          const messageVariations = isInternalMessage
            ? styles.internalMessage
            : styles.borrowerMessage;
          const contentStyling = isInternalMessage
            ? styles.internalMessageContent
            : styles.borrowerMessageContent;
          const bubbleColor = getBubbleColor(
            sentByCurrentUser,
            sentByOperator,
            sender_type === MessageSenders.System,
          );

          return (
            <div className={classnames(styles.comment, messageVariations)} key={id}>
              <div className={styles.commentTimestamp}>
                <div>
                  {formatDateTimeString(created_at)}
                  {" | "}
                  {isInternalMessage ? SENDING_MODE.INTERNAL : SENDING_MODE.BORROWER}
                </div>
              </div>
              <div className={styles.commentInner}>
                <div className={classnames(styles.sender, bubbleColor)}>
                  <span>{sentByOperator && !isInternalMessage && "HomeLight"}</span>

                  <span>{getBubbleText(sender_type, sender_name)}</span>
                </div>
                <div className={contentStyling}>{message}</div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default CommentsModal;
